import { useState, useEffect, useRef, useCallback } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CustomCursor from '@/components/feature/CustomCursor';
import Preloader from './components/Preloader';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import AboutSection from './components/AboutSection';
import AreasSection from './components/AreasSection';
import DifferentialsSection from './components/DifferentialsSection';
import JourneySection from './components/JourneySection';
import ClinicSection from './components/ClinicSection';
import TestimonialsSection from './components/TestimonialsSection';
import InstagramSection from './components/InstagramSection';
import FAQSection from './components/FAQSection';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
  const [loading, setLoading] = useState(true);
  const lenisRef = useRef<Lenis | null>(null);

  const handlePreloaderComplete = useCallback(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    if (loading) return;

    // Lenis with cinematic easing
    const lenis = new Lenis({
      duration: 1.8,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: true,
      touchMultiplier: 2,
    });

    lenisRef.current = lenis;

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    const rafId = requestAnimationFrame(raf);

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    // Cinematic cascade reveal — sections reveal their children with blur + fade
    const setupCascadeReveal = () => {
      const sections = document.querySelectorAll('[data-cascade]');

      sections.forEach((section) => {
        const label = section.querySelector('[data-cascade-label]');
        const line = section.querySelector('[data-cascade-line]');
        const title = section.querySelector('[data-cascade-title]');
        const text = section.querySelector('[data-cascade-text]');
        const buttons = section.querySelector('[data-cascade-buttons]');
        const image = section.querySelector('[data-cascade-image]');
        const items = section.querySelectorAll('[data-cascade-item]');

        const elements: (Element | null)[] = [];

        if (label) elements.push(label);
        if (line) elements.push(line);
        if (title) elements.push(title);
        if (text) elements.push(text);
        if (buttons) elements.push(buttons);
        if (image) elements.push(image);

        if (items.length > 0) {
          items.forEach((item) => elements.push(item));
        }

        if (elements.length === 0) return;

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 82%',
            end: 'top 18%',
            toggleActions: 'play none none reverse',
          },
        });

        elements.forEach((el, i) => {
          if (!el) return;
          tl.fromTo(
            el,
            { opacity: 0, y: 36, filter: 'blur(6px)' },
            { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.9, ease: 'power3.out' },
            i * 0.12
          );
        });
      });
    };

    // Side-slide animations with blur dissolve
    const setupSlideIn = () => {
      const slideItems = document.querySelectorAll('[data-slide-in]');

      slideItems.forEach((item) => {
        const direction = item.getAttribute('data-slide-direction') || 'left';
        const fromX = direction === 'right' ? 50 : -50;

        gsap.fromTo(
          item,
          { opacity: 0, x: fromX, filter: 'blur(4px)' },
          {
            opacity: 1,
            x: 0,
            filter: 'blur(0px)',
            duration: 1.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: item,
              start: 'top 88%',
              end: 'top 32%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    };

    const timer = setTimeout(() => {
      setupCascadeReveal();
      setupSlideIn();
    }, 600);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      ScrollTrigger.getAll().forEach((st) => st.kill());
      clearTimeout(timer);
    };
  }, [loading]);

  return (
    <>
      <Preloader onComplete={handlePreloaderComplete} />
      <CustomCursor />

      {!loading && (
        <main className="relative">
          {/* Fixed subtle gradient background for editorial depth */}
          <div
            className="fixed inset-0 -z-10 pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, #FAF8F6 0%, #F5EFE9 25%, #FAF8F6 50%, #F5EFE9 75%, #FAF8F6 100%)',
            }}
          />
          <Navbar />
          <Hero />
          <article>
            <AboutSection />
            <AreasSection />
            <DifferentialsSection />
            <JourneySection />
            <ClinicSection />
            <TestimonialsSection />
            <InstagramSection />
            <FAQSection />
            <FinalCTA />
          </article>
          <Footer />

          {/* Floating WhatsApp Button */}
          <a
            href="https://api.whatsapp.com/send/?phone=5585984293412&text=Ol%C3%A1%21+Gostaria+de+informa%C3%A7%C3%B5es+sobre+procedimentos+e+consultas+com+a+Dra+Nathalia&type=phone_number&app_absent=0"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-8 right-8 z-[999] w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] hover:bg-[#20bd5a] hover:scale-110 flex items-center justify-center shadow-[0_8px_30px_rgba(37,211,102,0.35)] hover:shadow-[0_12px_40px_rgba(37,211,102,0.5)] transition-all duration-400 group"
            aria-label="Fale conosco pelo WhatsApp"
          >
            <i className="ri-whatsapp-line text-white text-2xl md:text-3xl group-hover:scale-110 transition-transform duration-300" />
            {/* Pulse ring */}
            <span className="absolute inset-0 rounded-full border-2 border-[#25D366]/40 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
          </a>
        </main>
      )}

      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Physician',
            name: 'Dra. Nathália Posso',
            description: 'Ginecologista em Fortaleza. Atendimento humanizado e personalizado em Ginecologia, DIU, Reposição Hormonal, Laser Íntimo e Menopausa.',
            medicalSpecialty: 'Gynecology',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Av. Dom Luís, 1200, Sala 801',
              addressLocality: 'Fortaleza',
              addressRegion: 'CE',
              addressCountry: 'BR',
            },
            telephone: '(85) 99999-0000',
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '08:00',
                closes: '18:00',
              },
            ],
            url: 'https://www.dranathaliaposso.com.br',
          }),
        }}
      />
    </>
  );
}